import { useMemo } from 'react';
import { Dayjs } from 'dayjs';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useStableQueries } from '@frontend/react-query-helpers';
import { ScheduleRequestListItem, useScheduleRequestFeaturesAccess } from '@frontend/schedule-helpers';
import { DEFAULT_SCHEDULE_REQUEST_STATUSES_PARAM_V3 } from '../../../../../../constants';
import { getOneDayRangeFromNow } from '../../../../../../utils';
import { convertScheduleRequestV3ToV2 } from './useGetScheduleRequest';

type UseGetScheduleRequestListQueriesProps = {
  locationIds: string[];
  date: Dayjs | string;
};

export const useGetScheduleRequestListQueries = ({ locationIds, date }: UseGetScheduleRequestListQueriesProps) => {
  const { locationTimezoneMap } = SchedulerV3.Helpers.useScheduleLocationTimezoneShallowStore('locationTimezoneMap');

  const { isScheduleV3FlagEnabled, isLoading: isLoadingFF } = SchedulerV3.Helpers.useGetSchedulerV3FeatureFlagDetails();
  const { scheduleRequestFeatureEnabledLocationIds } = useScheduleRequestFeaturesAccess();

  const scheduleRequestListQueriesV3 = SchedulerV3.Queries.useListBookingSubmissionsQueries(
    locationIds.map((id) => {
      const dateRangeObj = getDateRangeObj({ date, timezone: locationTimezoneMap[id]?.timezone });
      const isScheduleRequestFeatureEnabledForLocation = scheduleRequestFeatureEnabledLocationIds.includes(id);
      return {
        request: {
          locationsIds: [id],
          reviewedStatuses: DEFAULT_SCHEDULE_REQUEST_STATUSES_PARAM_V3,
          ...dateRangeObj,
        },
        options: {
          enabled: !isLoadingFF && isScheduleV3FlagEnabled && isScheduleRequestFeatureEnabledForLocation,
          refetchOnMount: true,
          refetchOnWindowFocus: false,
          select: (data) => ({
            rows: (data.bookingSubmissions ?? [])
              .map<ScheduleRequestListItem>(convertScheduleRequestV3ToV2)
              .filter((request) => !request.aiRequestDetails),
          }),
        },
      };
    })
  );

  const stableScheduleRequestQueries = useStableQueries(scheduleRequestListQueriesV3);

  const scheduleRequests = useMemo(
    () => stableScheduleRequestQueries.map((query) => query.data?.rows ?? []).flat(),
    [stableScheduleRequestQueries]
  );

  const refetch = () => {
    scheduleRequestListQueriesV3?.forEach((query) => query.refetch());
  };
  return { queries: stableScheduleRequestQueries, scheduleRequests, refetch };
};

export type RequestListDateRangeParams = { date: Dayjs | string; timezone: string };

const getDateRangeObj = ({
  date,
  timezone,
}: RequestListDateRangeParams): { requestedSlotsBetween: { start: string; end: string } } | undefined => {
  if (!date) {
    return undefined;
  }
  const dateRangeArr = getOneDayRangeFromNow(date, timezone);
  const start = dateRangeArr[0];
  const end = dateRangeArr[1];
  return start && end ? { requestedSlotsBetween: { start, end } } : undefined;
};
